<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import { uploadImage } from "@/lib/uploads";
import { toDateTimeLocal } from "@/lib/date";
import { fetchPopup, fetchTopics, addPopup, editPopup } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");
const allTopics = ref([]);
const scheduleItems = ["Daily", "Weekly", "Monthly", "Yearly"];
const form = ref({ imgUrl: "", title: "", body: "", redirectUrl: "", topics: [], startDate: "", endDate: "", schedule: "", btnTitle: "", isExternalBrowser: false, isDynamic: false });
const imageUploader = (file, p) => uploadImage("popups", file, p);

onMounted(async () => {
  allTopics.value = await fetchTopics().catch(() => []);
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const p = await fetchPopup(id.value);
    if (!p) { notFound.value = true; return; }
    form.value = { imgUrl: p.imgUrl ?? "", title: p.title ?? "", body: p.body ?? "", redirectUrl: p.redirectUrl ?? "", topics: Array.isArray(p.topics) ? p.topics : [], startDate: toDateTimeLocal(p.startDate), endDate: toDateTimeLocal(p.endDate), schedule: p.schedule ?? "", btnTitle: p.btnTitle ?? "", isExternalBrowser: !!p.isExternalBrowser, isDynamic: !!p.isDynamic };
  } catch { error.value = "Couldn't load the popup."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const f = form.value;
  if (!f.title.trim()) { validationError.value = "Title is required."; return; }
  if (!f.body.trim()) { validationError.value = "Body is required."; return; }
  if (!f.startDate || !f.endDate) { validationError.value = "Start and end dates are required."; return; }
  if (!f.schedule) { validationError.value = "Schedule is required."; return; }
  if (!f.topics.length) { validationError.value = "Select at least one topic."; return; }
  if (!f.btnTitle.trim()) { validationError.value = "Button title is required."; return; }
  if (!f.redirectUrl.trim()) { validationError.value = "Button URL is required."; return; }
  if (!isEdit.value && !f.imgUrl) { validationError.value = "Upload a cover photo."; return; }
  const payload = { imgUrl: f.imgUrl, title: f.title, body: f.body, redirectUrl: f.redirectUrl, topics: f.topics, startDate: new Date(f.startDate), endDate: new Date(f.endDate), schedule: f.schedule, btnTitle: f.btnTitle, isExternalBrowser: f.isExternalBrowser, isDynamic: f.isDynamic };
  saving.value = true;
  try {
    if (isEdit.value) await editPopup(id.value, payload); else await addPopup(payload);
    success.value = true; setTimeout(() => router.push({ name: "popups" }), 800);
  } catch { error.value = "Error occurred. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'popups' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Popup" : "Create Popup" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Popup not found.</VAlert>
    <VCard v-else class="pa-4" max-width="720">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <UploadField v-model="form.imgUrl" kind="image" label="Cover Photo" :uploader="imageUploader" class="mb-4" />
        <VRow>
          <VCol cols="12" md="6"><VTextField v-model="form.startDate" label="Start Date" type="datetime-local" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.endDate" label="End Date" type="datetime-local" /></VCol>
          <VCol cols="12" md="6">
            <VSelect v-model="form.schedule" label="Schedule" :items="scheduleItems" placeholder="Select one" />
          </VCol>
          <VCol cols="12">
            <VAutocomplete v-model="form.topics" label="Topics" :items="allTopics.map(t => ({ title: t.topic, value: t.topic }))" item-title="title" item-value="value" multiple chips closable-chips auto-select-first />
          </VCol>
          <VCol cols="12"><VTextField v-model="form.title" label="Title" /></VCol>
          <VCol cols="12"><VTextarea v-model="form.body" label="Body" rows="4" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.btnTitle" label="Button Title" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.redirectUrl" label="Button URL" placeholder="https://example.com/" /></VCol>
          <VCol cols="6"><VSwitch v-model="form.isDynamic" label="Dynamic User ID" hide-details /></VCol>
          <VCol cols="6"><VSwitch v-model="form.isExternalBrowser" label="External Browser" hide-details /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving">Submit</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'popups' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
